/**
 * Canonical, machine-readable reasons a basket item can be unavailable at
 * validation time. Used by api, widget, webcomponents and dashboard.
 *
 * Codes group into UX buckets handled by the widget:
 *   - PARTIAL_CAPACITY: render "only N left" + Reduce-to-N action
 *   - SOLD_OUT: render hard error + Remove action
 *   - EXPIRED: time-based, no recovery; Remove + (optional) Next-session link
 *   - CANCELLED: school-cancelled; Remove + apology copy
 *   - GONE: record deleted, or an item type we don't recognise; Remove silently
 *   - MEMBERS_ONLY: upsell tone (blue, not red); Join CTA
 *   - SCHOOL_ERROR: payment config / multi-school; not the buyer's fault
 *   - TRANSIENT: backend hiccup; Retry, do not suggest removal
 */
export type BasketUnavailabilityCode =
  // capacity / stock
  | "SESSION_SOLD_OUT"
  | "SESSION_PARTIAL_CAPACITY"
  | "APPOINTMENT_SLOT_TAKEN"
  // equipment: the ticket's resource pool has no free units for the window —
  // seats may well be free; the wording must say equipment, not spots
  | "RESOURCE_POOL_SOLD_OUT"
  // config: pool-carrying tickets cannot be sold through bundles
  | "POOL_TICKET_NOT_BUNDLEABLE"
  | "PRODUCT_OUT_OF_STOCK"
  | "PRODUCT_PARTIAL_STOCK"
  // time
  | "SESSION_STARTED"
  | "BOOKING_CUTOFF"
  | "TICKET_SALE_ENDED"
  // school action
  | "SESSION_CANCELLED"
  | "COURSE_UNAVAILABLE"
  | "PRODUCT_DISABLED"
  | "BUNDLE_DISABLED"
  // deleted
  | "SESSION_NOT_FOUND"
  | "COURSE_NOT_FOUND"
  | "PRODUCT_NOT_FOUND"
  | "BUNDLE_NOT_FOUND"
  // membership
  | "MEMBERSHIP_INACTIVE"
  | "TICKET_MEMBERS_ONLY"
  | "TICKET_TAG_GATED"
  // ticket validation rules
  | "TICKET_CONSTRAINT_VIOLATION"
  // per-customer trial allowance (course.numberOfTrialsPerCustomer) —
  // counts existing trial bookings AND the other trial items in this basket
  | "TRIAL_LIMIT_REACHED"
  // checkout-level
  | "MULTIPLE_SCHOOLS"
  | "SCHOOL_PAYMENT_NOT_READY"
  // catch-alls
  | "VALIDATION_TRANSIENT"
  | "UNKNOWN_ITEM_TYPE";

export type BasketUnavailabilityBucket =
  | "partial"
  | "sold_out"
  | "expired"
  | "cancelled"
  | "gone"
  | "members_only"
  | "school_error"
  | "transient";

export type BasketUnavailability =
  | {
      code: "SESSION_PARTIAL_CAPACITY";
      availableQuantity: number;
      requestedQuantity: number;
    }
  | {
      code: "PRODUCT_PARTIAL_STOCK";
      availableQuantity: number;
      requestedQuantity: number;
    }
  | { code: "SESSION_SOLD_OUT" }
  | { code: "APPOINTMENT_SLOT_TAKEN" }
  | { code: "RESOURCE_POOL_SOLD_OUT" }
  /**
   * NOT CURRENTLY EMITTED. The validate-time guard that produced it read
   * `item.ticketSelections`, which a bundle item never carries, so it was
   * dead code that read as protection. Kept as the landing spot for a real
   * guard — at config time (refuse attaching a pool to a ticket on a
   * bundle-eligible course) or at redemption in ChargeBlock, where the ticket
   * is finally known.
   */
  | { code: "POOL_TICKET_NOT_BUNDLEABLE" }
  | { code: "PRODUCT_OUT_OF_STOCK" }
  | { code: "SESSION_STARTED"; startedAt: string }
  | { code: "BOOKING_CUTOFF"; closesHoursBefore: number; startsAt: string }
  | { code: "TICKET_SALE_ENDED"; closesHoursBefore: number }
  | { code: "SESSION_CANCELLED" }
  | { code: "COURSE_UNAVAILABLE"; status: "draft" | "archived" }
  | { code: "PRODUCT_DISABLED" }
  | { code: "BUNDLE_DISABLED" }
  | { code: "SESSION_NOT_FOUND" }
  | { code: "COURSE_NOT_FOUND" }
  | { code: "PRODUCT_NOT_FOUND" }
  | { code: "BUNDLE_NOT_FOUND" }
  | { code: "MEMBERSHIP_INACTIVE" }
  | { code: "TICKET_MEMBERS_ONLY"; requiredMembershipPlanIds?: string[] }
  | { code: "TICKET_TAG_GATED"; requiredCustomerTags?: string[] }
  | { code: "TICKET_CONSTRAINT_VIOLATION"; reason: string }
  /**
   * `limit` is the course's per-customer allowance and `remaining` what is
   * left for the whole basket. Items past the remaining allowance carry this.
   *
   * `spent` says WHERE the allowance went, deliberately as a category rather
   * than a count: `/widget/basket/validate` is unauthenticated and takes the
   * buyer email from the request, so echoing "this address has used 3 trials
   * on this listing" would turn it into an oracle over other people's
   * booking history. The copy only ever needed the category. (A gate keyed
   * on an unproven email inevitably reveals whether that email is eligible —
   * `TICKET_MEMBERS_ONLY` has the same property — but it need not hand out
   * counts on top.)
   */
  | {
      code: "TRIAL_LIMIT_REACHED";
      limit: number;
      remaining: number;
      spent: "basket" | "history" | "both";
    }
  | { code: "MULTIPLE_SCHOOLS" }
  | {
      code: "SCHOOL_PAYMENT_NOT_READY";
      reason:
        | "not_validated"
        | "stripe_missing"
        | "charges_disabled"
        | "currency_mismatch";
    }
  | { code: "VALIDATION_TRANSIENT" }
  | { code: "UNKNOWN_ITEM_TYPE" };

export function getUnavailabilityBucket(
  u: BasketUnavailability,
): BasketUnavailabilityBucket {
  switch (u.code) {
    case "SESSION_PARTIAL_CAPACITY":
    case "PRODUCT_PARTIAL_STOCK":
      return "partial";
    case "SESSION_SOLD_OUT":
    case "APPOINTMENT_SLOT_TAKEN":
    case "RESOURCE_POOL_SOLD_OUT":
    case "POOL_TICKET_NOT_BUNDLEABLE":
    case "PRODUCT_OUT_OF_STOCK":
    case "TICKET_CONSTRAINT_VIOLATION":
    case "TICKET_TAG_GATED":
    case "TRIAL_LIMIT_REACHED":
      // TRIAL_LIMIT_REACHED sits with the other eligibility gates: "this
      // customer isn't eligible for this ticket" — non-retryable, Remove is
      // the remedy. Not members_only: there is no upsell that unlocks it.
      return "sold_out";
    case "SESSION_STARTED":
    case "BOOKING_CUTOFF":
    case "TICKET_SALE_ENDED":
      return "expired";
    case "SESSION_CANCELLED":
    case "COURSE_UNAVAILABLE":
    case "PRODUCT_DISABLED":
    case "BUNDLE_DISABLED":
      return "cancelled";
    case "SESSION_NOT_FOUND":
    case "COURSE_NOT_FOUND":
    case "PRODUCT_NOT_FOUND":
    case "BUNDLE_NOT_FOUND":
      return "gone";
    case "MEMBERSHIP_INACTIVE":
    case "TICKET_MEMBERS_ONLY":
      return "members_only";
    case "MULTIPLE_SCHOOLS":
    case "SCHOOL_PAYMENT_NOT_READY":
      return "school_error";
    case "VALIDATION_TRANSIENT":
      return "transient";
    // NOT transient, despite reading like one. `transient` means the CHECK
    // failed and retrying may succeed, which is why the bucket suppresses
    // Remove and offers Retry, and why both baskets let it through to
    // checkout. This code means the server classified the item fine and did
    // not recognise its `type` — retrying returns the same answer forever, and
    // removal is the remedy. Bucketing it as transient gave the drawer an
    // amber "we couldn't check availability" over a red "this can't be checked
    // out", contradicting each other about one item.
    case "UNKNOWN_ITEM_TYPE":
      return "gone";
    // Unreachable per the union today, but a client bundle compiled before a
    // new code was added can receive it at runtime (API deploys first).
    // Unknown codes only ever ride on unavailable items, so hard-block.
    default:
      return "sold_out";
  }
}

/**
 * Server-side fallback copy. The widget overrides this per code with
 * action-oriented strings; keep these terse and human so callers that
 * surface the raw `message` field (legacy paths) still read sensibly.
 */
export function defaultUnavailabilityMessage(u: BasketUnavailability): string {
  switch (u.code) {
    case "SESSION_PARTIAL_CAPACITY":
      return `Only ${u.availableQuantity} ${u.availableQuantity === 1 ? "spot" : "spots"} remaining`;
    case "PRODUCT_PARTIAL_STOCK":
      return `Only ${u.availableQuantity} in stock`;
    case "SESSION_SOLD_OUT":
      return "This session is fully booked";
    case "APPOINTMENT_SLOT_TAKEN":
      return "This time slot is no longer available";
    case "RESOURCE_POOL_SOLD_OUT":
      return "The equipment for this booking has just been taken";
    case "POOL_TICKET_NOT_BUNDLEABLE":
      return "This ticket can't be bought as part of a bundle";
    case "PRODUCT_OUT_OF_STOCK":
      return "Out of stock";
    case "SESSION_STARTED":
      return "This session has already started";
    case "BOOKING_CUTOFF":
      return `Bookings for this session closed ${u.closesHoursBefore} ${u.closesHoursBefore === 1 ? "hour" : "hours"} before the start time`;
    case "TICKET_SALE_ENDED":
      return `Sales for this ticket closed ${u.closesHoursBefore} ${u.closesHoursBefore === 1 ? "hour" : "hours"} before the start time`;
    case "SESSION_CANCELLED":
      return "The school cancelled this session";
    case "COURSE_UNAVAILABLE":
      return "This experience is no longer available";
    case "PRODUCT_DISABLED":
      return "This product is no longer available";
    case "BUNDLE_DISABLED":
      return "This bundle is no longer available";
    case "SESSION_NOT_FOUND":
    case "COURSE_NOT_FOUND":
    case "PRODUCT_NOT_FOUND":
    case "BUNDLE_NOT_FOUND":
      return "This item is no longer offered";
    case "MEMBERSHIP_INACTIVE":
      return "Your membership is no longer active";
    case "TICKET_MEMBERS_ONLY":
      return "This ticket is available to members only";
    case "TICKET_TAG_GATED":
      return "This ticket is limited to selected customers";
    case "TICKET_CONSTRAINT_VIOLATION":
      return u.reason;
    case "TRIAL_LIMIT_REACHED": {
      // Worded on `remaining`, not `used`: `used` is only the persisted
      // count, so when the limit is hit by other trial items in the SAME
      // basket "you've used 0" would contradict the block.
      if (u.remaining === 0) {
        // Nothing booked before — it's the rest of this basket that spends
        // the allowance. Telling a first-time buyer they've "already used"
        // their trial is simply false.
        if (u.spent === "basket") {
          return u.limit === 1
            ? "The other trial ticket in your basket uses your trial for this class"
            : `Your basket already has all ${u.limit} trial tickets you can book for this class`;
        }
        // Partly booked, partly in the basket — "you've used all N" would
        // overstate what the customer actually took.
        if (u.spent === "both") {
          return "Your basket has more trial tickets for this class than your remaining allowance";
        }
        return u.limit === 1
          ? "You've already used your trial for this class"
          : `You've used all ${u.limit} trials for this class`;
      }
      // Nothing used at all — one line simply asks for more than the whole
      // allowance ("only 2 of your 2 are left" would read as no problem).
      if (u.remaining >= u.limit) {
        return `You can book at most ${u.limit} trial ${u.limit === 1 ? "ticket" : "tickets"} for this class`;
      }
      // Some allowance is left, but this line asks for more than it. Say WHO
      // spent the rest: "only 1 of your 2 is left" tells a first-time buyer
      // they have already taken one, when it is the basket that took it.
      if (u.spent === "basket") {
        return `Your basket already uses ${u.limit - u.remaining} of your ${u.limit} trials for this class`;
      }
      return `Only ${u.remaining} of your ${u.limit} trials for this class ${u.remaining === 1 ? "is" : "are"} left`;
    }
    case "MULTIPLE_SCHOOLS":
      return "Your basket has items from more than one school";
    case "SCHOOL_PAYMENT_NOT_READY":
      return "We couldn't process this checkout. Contact the school.";
    case "VALIDATION_TRANSIENT":
      return "We couldn't check availability — try again";
    case "UNKNOWN_ITEM_TYPE":
      return "We couldn't recognise this item";
    // Runtime guard for codes newer than this bundle — see getUnavailabilityBucket.
    default:
      return "This item can't be booked right now";
  }
}
